import React, { useState, useEffect, useCallback } from 'react';
import {
    MdAdd, MdClose, MdInbox, MdCheckCircle,
    MdReport, MdSend, MdRefresh, MdReply,
    MdDownload, MdVisibility
} from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ── Constants ─────────────────────────────────────────────────────────────────

const REPORT_TYPES = [
    { value: 'incident', label: 'Incident' },
    { value: 'maintenance_request', label: 'Maintenance Request' },
    { value: 'safety', label: 'Safety Issue' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'complaint', label: 'Complaint' },
    { value: 'system_issue', label: 'System Issue' },
    { value: 'other', label: 'Other' },
];

const STATUS_META = {
    new: { label: 'New', bg: '#dbeafe', color: '#1d4ed8' },
    read: { label: 'Read', bg: '#f3f4f6', color: '#6b7280' },
    in_review: { label: 'In Review', bg: '#fef9c3', color: '#b45309' },
    resolved: { label: 'Resolved', bg: '#dcfce7', color: '#15803d' },
    dismissed: { label: 'Dismissed', bg: '#fee2e2', color: '#b91c1c' },
};

// Only these four statuses have no auto-translate dictionary entry; the rest
// (e.g. "In Review") are translated by the translateUiText DOM walker.
const STATUS_LABEL_KEYS = {
    new: 'reports.statusNew',
    read: 'reports.statusRead',
    resolved: 'reports.statusResolved',
    dismissed: 'reports.statusDismissed',
};

const PRIORITY_META = {
    low: { label: 'Low', bg: '#f0fdf4', color: '#15803d' },
    medium: { label: 'Medium', bg: '#fef9c3', color: '#b45309' },
    high: { label: 'High', bg: '#fff7ed', color: '#ea580c' },
    urgent: { label: 'Urgent', bg: '#fee2e2', color: '#b91c1c' },
};

function StatusBadge({ status }) {
    const { t } = useLanguage();
    const m = STATUS_META[status] || { label: status, bg: '#f1f5f9', color: '#475569' };
    const label = STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : m.label;
    return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{label}</span>;
}

function PriorityBadge({ priority }) {
    const m = PRIORITY_META[priority] || { label: priority, bg: '#f1f5f9', color: '#475569' };
    return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: m.bg, color: m.color, textTransform: 'uppercase' }}>{m.label}</span>;
}

function typeLabel(val, t) {
    if (val === 'incident' && t) return t('reports.typeIncident');
    return REPORT_TYPES.find(rt => rt.value === val)?.label || val;
}

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}>
                        <MdClose size={20} />
                    </button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

// ── PDF download helper ───────────────────────────────────────────────────────

function downloadReportsPDF(reports, facilityName) {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.setTextColor(14, 165, 233);
    doc.text('MITI FMS — Employee Reports', 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text(`Facility: ${facilityName || 'All Facilities'}`, 14, 26);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);

    autoTable(doc, {
        startY: 40,
        head: [['#', 'From', 'Subject', 'Type', 'Priority', 'Status', 'Submitted', 'Reply']],
        body: reports.map((r, i) => [
            i + 1,
            `${r.submitted_by_name}${r.job_title ? ` (${r.job_title})` : ''}`,
            r.subject,
            typeLabel(r.report_type),
            r.priority,
            r.status.replace('_', ' '),
            new Date(r.created_at).toLocaleDateString(),
            r.manager_reply ? 'Yes' : 'No',
        ]),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [14, 165, 233], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 2: { cellWidth: 45 }, 3: { cellWidth: 32 } },
    });

    // Report details section
    let y = doc.lastAutoTable.finalY + 12;
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    doc.text('Report Details', 14, y);
    y += 6;

    reports.forEach((r, i) => {
        if (y > 260) { doc.addPage(); y = 18; }
        doc.setFontSize(10);
        doc.setTextColor(14, 165, 233);
        doc.text(`${i + 1}. ${r.subject}`, 14, y += 8);
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(9);
        doc.text(`From: ${r.submitted_by_name} | Type: ${typeLabel(r.report_type)} | Priority: ${r.priority} | Status: ${r.status}`, 18, y += 5);
        doc.setTextColor(30, 41, 59);
        const desc = doc.splitTextToSize(r.description, 170);
        doc.text(desc, 18, y += 5);
        y += (desc.length - 1) * 4;
        if (r.manager_reply) {
            doc.setTextColor(2, 132, 199);
            const reply = doc.splitTextToSize(`Manager Reply: ${r.manager_reply}`, 170);
            doc.text(reply, 18, y += 5);
            y += (reply.length - 1) * 4;
        }
        y += 3;
    });

    doc.save(`Reports_${facilityName || 'All'}_${new Date().toISOString().split('T')[0]}.pdf`);
}

// ── Employee View ─────────────────────────────────────────────────────────────

function EmployeeReports() {
    const { t } = useLanguage();
    const { isEmployee } = useAuth();
    const [reports, setReports] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ subject: '', report_type: 'other', priority: 'medium', description: '' });

    const load = async () => {
        setLoading(true);
        try { const { data } = await API.get('/reports/mine'); setReports(data); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        API.get('/reports/eligibility').then(response => setBookings(response.data.bookings || []));
    }, []);

    const handleSubmit = async e => {
        e.preventDefault(); setSaving(true);
        try {
            await API.post('/reports', { ...form, booking_id: form.booking_id || null });
            setForm({ subject: '', report_type: 'other', priority: 'medium', description: '', booking_id: '' });
            setShowForm(false); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Submit failed')); }
        finally { setSaving(false); }
    };

    const counts = {
        all: reports.length,
        pending: reports.filter(r => r.status === 'new').length,
        review: reports.filter(r => r.status === 'in_review').length,
        resolved: reports.filter(r => r.status === 'resolved').length,
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>My Reports</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Submit and track your reports to the facility manager</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-light btn-sm" onClick={load}><MdRefresh size={16} /></button>
                    <button className="btn btn-primary" onClick={() => setShowForm(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdAdd size={18} /> New Report
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
                {[
                    { label: 'Total Submitted', value: counts.all, icon: <MdReport />, cls: 'blue' },
                    { label: t('reports.statPending'), value: counts.pending, icon: <MdReport />, cls: 'yellow' },
                    { label: 'In Review', value: counts.review, icon: <MdReport />, cls: 'teal' },
                    { label: 'Resolved', value: counts.resolved, icon: <MdCheckCircle />, cls: 'green' },
                ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                        <div className="stat-card">
                            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
                            <div><div className="stat-value">{s.value}</div><div className="stat-label">{s.label}</div></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>Subject</th><th>Type</th><th>Priority</th><th>Status</th>
                                    <th style={{ width: 120 }}>Submitted</th><th style={{ width: 80 }}>Reply</th>
                                    <th style={{ width: 60 }}>View</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map(r => (
                                    <tr key={r.id} style={{ fontWeight: r.status === 'new' ? 600 : 'normal' }}>
                                        <td style={{ maxWidth: 220 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500 }}>{r.subject}</div>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{typeLabel(r.report_type, t)}</td>
                                        <td><PriorityBadge priority={r.priority} /></td>
                                        <td><StatusBadge status={r.status} /></td>
                                        <td style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                            {new Date(r.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            {r.manager_reply
                                                ? <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>✓ Replied</span>
                                                : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-light" onClick={() => setSelected(r)} title="View details">
                                                <MdVisibility size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!reports.length && (
                                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 50, color: '#94a3b8' }}>
                                        <MdReport size={36} style={{ color: '#cbd5e1', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                        No reports submitted yet
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* View report modal */}
            {selected && (
                <Modal title={selected.subject} onClose={() => setSelected(null)}>
                    <div className="row g-2 mb-3" style={{ fontSize: 13 }}>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Type</span><br /><strong>{typeLabel(selected.report_type, t)}</strong></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Priority</span><br /><PriorityBadge priority={selected.priority} /></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Status</span><br /><StatusBadge status={selected.status} /></div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Submitted</span><br />{new Date(selected.created_at).toLocaleString()}</div>
                    </div>
                    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', fontSize: 13, marginBottom: 16, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                        {selected.description}
                    </div>
                    {selected.manager_reply && (
                        <div style={{ background: '#e0f2fe', borderRadius: 10, padding: '12px 14px', borderLeft: '3px solid #0ea5e9' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1', marginBottom: 6 }}>
                                <MdReply size={14} style={{ marginRight: 4 }} />
                                {t('reports.managersReply')}{selected.replied_at ? ` · ${new Date(selected.replied_at).toLocaleString()}` : ''}
                            </div>
                            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{selected.manager_reply}</div>
                        </div>
                    )}
                    <button className="btn btn-light w-100 mt-3" onClick={() => setSelected(null)}>Close</button>
                </Modal>
            )}

            {/* New report modal */}
            {showForm && (
                <Modal title="Submit a Report" onClose={() => setShowForm(false)}>
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">Subject *</label>
                                <input className="form-control" placeholder="Brief description of the issue"
                                    value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Report Type</label>
                                <select className="form-select" value={form.report_type}
                                    onChange={e => setForm({ ...form, report_type: e.target.value })}>
                                    {REPORT_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.value === 'incident' ? t('reports.typeIncident') : rt.label}</option>)}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('reports.relatedSpaceBooking')} {isEmployee ? '(optional)' : '*'}</label>
                                <select className="form-select" value={form.booking_id || ''}
                                    onChange={e => setForm({ ...form, booking_id: e.target.value })} required={!isEmployee}>
                                    <option value="">Select a registered booking</option>
                                    {bookings.map(booking => <option key={booking.booking_id} value={booking.booking_id}>
                                        {booking.space_name} at {booking.facility_name} ({new Date(booking.start_datetime).toLocaleDateString()})
                                    </option>)}
                                </select>
                                {!isEmployee && !bookings.length && <small className="text-danger">No eligible registered bookings found.</small>}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Priority</label>
                                <select className="form-select" value={form.priority}
                                    onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">{t('reports.priorityUrgentEmoji')}</option>
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">Description *</label>
                                <textarea className="form-control" rows={5}
                                    placeholder="Describe the issue — location, what happened, when it started..."
                                    value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowForm(false)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Sending...</>
                                    : <><MdSend size={16} /> Submit Report</>}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

// ── Manager View ──────────────────────────────────────────────────────────────

function ManagerReports({ developerView = false }) {
    const { t } = useLanguage();
    const { assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState(developerView ? 'system_issue' : '');
    const [selected, setSelected] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [saving, setSaving] = useState(false);
    const [showIssueForm, setShowIssueForm] = useState(false);
    const [issueForm, setIssueForm] = useState({ subject: '', priority: 'medium', description: '' });
    const [issueSaving, setIssueSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterStatus) params.status = filterStatus;
            if (filterType) params.report_type = filterType;
            const { data } = await API.get('/reports', { params });
            setReports(data);
        } finally { setLoading(false); }
    }, [filterStatus, filterType]);

    useEffect(() => { load(); }, [load]);

    const openReport = async r => {
        setSelected(r);
        setReplyText(r.manager_reply || '');
        setNewStatus(r.status);
        if (r.status === 'new') {
            await API.put(`/reports/${r.id}`, { status: 'read' });
            load();
        }
    };

    const handleSave = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await API.put(`/reports/${selected.id}`, { status: newStatus, manager_reply: replyText });
            setSelected(null); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); }
        finally { setSaving(false); }
    };

    const handleDownload = () => downloadReportsPDF(reports, assignedFacility?.name);

    const handleIssueSubmit = async e => {
        e.preventDefault(); setIssueSaving(true);
        try {
            await API.post('/reports', { ...issueForm, report_type: 'system_issue' });
            setIssueForm({ subject: '', priority: 'medium', description: '' });
            setShowIssueForm(false); load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Submit failed')); }
        finally { setIssueSaving(false); }
    };

    const newCount = reports.filter(r => r.status === 'new').length;
    const counts = {
        all: reports.length,
        new: reports.filter(r => r.status === 'new').length,
        review: reports.filter(r => r.status === 'in_review').length,
        resolved: reports.filter(r => r.status === 'resolved').length,
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <MdInbox style={{ color: '#0ea5e9' }} /> {developerView ? 'System Issues' : 'Reports Inbox'}
                        {newCount > 0 && (
                            <span style={{ background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                                {newCount} new
                            </span>
                        )}
                    </h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {developerView ? 'Software issues reported by staff' : t('reports.facilityReportsSubtitle', '', { facility: assignedFacility?.name || 'Your facility' })}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-light btn-sm" onClick={load}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MdRefresh size={16} /> Refresh
                    </button>
                    {!developerView && (
                        <button className="btn btn-light btn-sm" onClick={() => setShowIssueForm(true)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdAdd size={16} /> Report System Issue
                        </button>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={handleDownload}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        disabled={reports.length === 0}>
                        <MdDownload size={16} /> Download PDF
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
                {[
                    { label: 'Total Reports', value: counts.all, icon: <MdReport />, cls: 'blue' },
                    { label: 'New', value: counts.new, icon: <MdReport />, cls: 'red' },
                    { label: 'In Review', value: counts.review, icon: <MdReport />, cls: 'yellow' },
                    { label: 'Resolved', value: counts.resolved, icon: <MdCheckCircle />, cls: 'green' },
                ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                        <div className="stat-card">
                            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
                            <div><div className="stat-value">{s.value}</div><div className="stat-label">{s.label}</div></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="page-card mb-4">
                <div style={{ padding: '12px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <select className="form-select form-select-sm" style={{ width: 150 }}
                        value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="">All Status</option>
                        {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{STATUS_LABEL_KEYS[v] ? t(STATUS_LABEL_KEYS[v]) : m.label}</option>)}
                    </select>
                    <select className="form-select form-select-sm" style={{ width: 200 }}
                        value={filterType} onChange={e => setFilterType(e.target.value)}>
                        <option value="">All Types</option>
                        {REPORT_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.value === 'incident' ? t('reports.typeIncident') : rt.label}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? <div className="spinner-wrap"><div className="spinner-border" /></div> : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>From</th><th>Space / Facility</th><th>Subject</th><th>Type</th><th>Priority</th>
                                    <th style={{ width: 110 }}>Status</th><th style={{ width: 100 }}>Submitted</th>
                                    <th style={{ width: 70 }}>Reply</th><th style={{ width: 80 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports.map(r => (
                                    <tr key={r.id} style={{ fontWeight: r.status === 'new' ? 700 : 'normal' }}>
                                        <td>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{r.submitted_by_name || r.guest_name || 'Guest'}</div>
                                            {r.job_title && <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{r.job_title}</div>}
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.submitted_by_email || r.guest_email || t('reports.guestBookingFallback')}</div>
                                        </td>
                                        <td style={{ fontSize: 12 }}>
                                            <div style={{ fontWeight: 600 }}>{r.space_name || t('reports.facilityReportFallback')}</div>
                                            <div style={{ color: '#64748b' }}>{r.facility_name || '—'}{r.floor != null ? ` · Floor ${r.floor}` : ''}{r.room_number ? ` · Room ${r.room_number}` : ''}</div>
                                        </td>
                                        <td style={{ maxWidth: 220 }}>
                                            <div style={{ fontSize: 13 }}>{r.subject}</div>
                                        </td>
                                        <td style={{ fontSize: 12 }}>{typeLabel(r.report_type, t)}</td>
                                        <td><PriorityBadge priority={r.priority} /></td>
                                        <td><StatusBadge status={r.status} /></td>
                                        <td style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                            {new Date(r.created_at).toLocaleDateString()}
                                        </td>
                                        <td>
                                            {r.manager_reply
                                                ? <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>✓ Sent</span>
                                                : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-primary" onClick={() => openReport(r)}
                                                style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                <MdReply size={13} /> Review
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!reports.length && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 50, color: '#94a3b8' }}>
                                        <MdInbox size={36} style={{ color: '#cbd5e1', marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                        No reports received yet
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Review + Reply Modal */}
            {selected && (
                <Modal title={`Report: ${selected.subject}`} onClose={() => setSelected(null)}>
                    {/* Meta */}
                    <div className="row g-2 mb-3" style={{ fontSize: 13 }}>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>From</span><br />
                            <strong>{selected.submitted_by_name}</strong>
                            {selected.job_title && <span style={{ fontSize: 11, color: '#94a3b8' }}> ({selected.job_title})</span>}
                        </div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Submitted</span><br />
                            {new Date(selected.created_at).toLocaleString()}
                        </div>
                        <div className="col-12" style={{ background: '#f0f9ff', borderRadius: 8, padding: 12 }}>
                            <strong>Booking and Location</strong><br />
                            <span>{selected.guest_name || selected.submitted_by_name || 'Guest'} · {selected.guest_email || selected.submitted_by_email || t('reports.noEmailFallback')}</span><br />
                            <span>{selected.space_name || 'No linked space'}{selected.facility_name ? ` · ${selected.facility_name}` : ''}{selected.floor != null ? ` · Floor ${selected.floor}` : ''}{selected.room_number ? ` · Room ${selected.room_number}` : ''}</span>
                            {selected.booking_start && <><br /><span>{new Date(selected.booking_start).toLocaleString()} – {new Date(selected.booking_end).toLocaleString()}</span></>}
                            {selected.booking_id && <><br /><span>Booking #{selected.booking_id} · {selected.booking_status} · Payment: {selected.payment_status || 'unpaid'} ({selected.currency || currency} {Number(selected.amount_paid || 0).toLocaleString()} / {Number(selected.total_amount || 0).toLocaleString()})</span></>}
                        </div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Type</span><br />
                            <strong>{typeLabel(selected.report_type, t)}</strong>
                        </div>
                        <div className="col-6"><span style={{ color: '#94a3b8' }}>Priority</span><br />
                            <PriorityBadge priority={selected.priority} />
                        </div>
                    </div>

                    {/* Description */}
                    <div style={{
                        background: '#f8fafc', borderRadius: 10, padding: '12px 14px', fontSize: 13,
                        marginBottom: 16, whiteSpace: 'pre-wrap', lineHeight: 1.7, maxHeight: 180, overflowY: 'auto'
                    }}>
                        {selected.description}
                    </div>

                    {/* Update form */}
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label fw-semibold">Update Status</label>
                            <select className="form-select" value={newStatus}
                                onChange={e => setNewStatus(e.target.value)}>
                                {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{STATUS_LABEL_KEYS[v] ? t(STATUS_LABEL_KEYS[v]) : m.label}</option>)}
                            </select>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold">Reply to Employee</label>
                            <textarea className="form-control" rows={4}
                                placeholder="Your response will be visible to the employee..."
                                value={replyText} onChange={e => setReplyText(e.target.value)} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button className="btn btn-light" onClick={() => setSelected(null)}>Cancel</button>
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving...</>
                                : <><MdSend size={16} /> Save & Reply</>}
                        </button>
                    </div>
                </Modal>
            )}

            {/* Report System Issue modal (admin/manager only) */}
            {showIssueForm && (
                <Modal title="Report a System Issue" onClose={() => setShowIssueForm(false)}>
                    <form onSubmit={handleIssueSubmit}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">Subject *</label>
                                <input className="form-control" placeholder="Brief description of the software issue"
                                    value={issueForm.subject} onChange={e => setIssueForm({ ...issueForm, subject: e.target.value })} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Priority</label>
                                <select className="form-select" value={issueForm.priority}
                                    onChange={e => setIssueForm({ ...issueForm, priority: e.target.value })}>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="urgent">{t('reports.priorityUrgentEmoji')}</option>
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">Description *</label>
                                <textarea className="form-control" rows={5}
                                    placeholder="Describe the bug — what happened, steps to reproduce, when it started..."
                                    value={issueForm.description} onChange={e => setIssueForm({ ...issueForm, description: e.target.value })} required />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowIssueForm(false)}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={issueSaving}
                                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {issueSaving ? <><span className="spinner-border spinner-border-sm me-2" />Sending...</>
                                    : <><MdSend size={16} /> Submit to Developer</>}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

// ── Route based on role ───────────────────────────────────────────────────────
export default function Reports() {
    const { isManager, isAdmin, isDeveloper } = useAuth();
    if (isDeveloper) return <ManagerReports developerView />;
    if (isManager || isAdmin) return <ManagerReports />;
    return <EmployeeReports />;
}
